import { PayloadAction, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import {
  ChatConnectionDetails,
  ChatMessage,
  ChatMessageBasic,
  ChatMessageStatus,
  ChatState as ChatConnState,
} from 'domain/Chat';
import { eventRoomActions } from 'pages/EventRoom/state';

import { ChatState } from './types';

const CHAT_HISTORY_LIMIT = 1000;

export const messagesAdapter = createEntityAdapter({
  selectId: (message: ChatMessage) => message.id,
});

export const initialState: ChatState = {
  messages: messagesAdapter.getInitialState(),
  isChatActive: false,
  isChatScrolledToBottom: false,
  unreadMessagesCount: 0,
  chatInputText: '',
  connectionDetails: null,
  v2ConnectionState: ChatConnState.Initial,
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatActive: (state, action: PayloadAction<boolean>) => {
      state.isChatActive = action.payload;
      if (state.isChatActive) {
        state.unreadMessagesCount = 0;
      }
    },
    setChatScrolledToBottom: (state, action: PayloadAction<boolean>) => {
      state.isChatScrolledToBottom = action.payload;
      if (state.isChatScrolledToBottom) {
        state.unreadMessagesCount = 0;
      }
    },
    excludeFromMessages: (state, action: PayloadAction<{ messages: ChatMessageBasic[] }>) => {
      messagesAdapter.removeMany(
        state.messages,
        action.payload.messages.map(({ id }) => id)
      );
    },
    markMessagesAsRejected: (state, { payload }: PayloadAction<{ messages: ChatMessageBasic[] }>) => {
      messagesAdapter.updateMany(
        state.messages,
        payload.messages.map(({ id }) => ({
          id,
          changes: {
            status: ChatMessageStatus.Rejected,
          },
        }))
      );
      if (!state.isChatActive) {
        state.unreadMessagesCount += payload.messages.length;
      }
    },
    addMessages: (
      state,
      action: PayloadAction<{
        messages: ChatMessageBasic[];
        status: ChatMessageStatus;
        accountId?: number;
        /** History backfill (e.g. v2 reconnect/reload): added for context, never counted as unread or animated. */
        isBackfill?: boolean;
      }>
    ) => {
      const { messages, status, isBackfill } = action.payload;
      // `addMany` ignores an id already in the store, so count what actually lands. A message the
      // store already holds must not bump the badge again: approval delivers the same message twice
      // (`MessagesApproved` and `ChatMessages`, either order), and only one of them inserts.
      const insertedCount = messages.filter(({ id }) => state.messages.entities[id] === undefined).length;
      const newMessages = messages.map(
        (message): ChatMessage => ({
          ...message,
          timestamp: 'timestamp' in message ? message.timestamp : Date.now(),
          isNew: false,
          status,
          shouldAnimate: !isBackfill && state.isChatActive && state.isChatScrolledToBottom,
        })
      );
      messagesAdapter.addMany(state.messages, newMessages);

      const numberOfMessagesToRemove = state.messages.ids.length - CHAT_HISTORY_LIMIT;
      if (numberOfMessagesToRemove > 0) {
        messagesAdapter.removeMany(state.messages, state.messages.ids.slice(0, numberOfMessagesToRemove));
      }

      if (!isBackfill && (!state.isChatActive || !state.isChatScrolledToBottom)) {
        state.unreadMessagesCount += insertedCount;
      }
    },
    markAsReadMessage: (state, action: PayloadAction<{ id: number | string }>) => {
      messagesAdapter.updateOne(state.messages, {
        id: action.payload.id,
        changes: {
          shouldAnimate: false,
        },
      });
    },
    setChatInputText: (state, action: PayloadAction<string>) => {
      state.chatInputText = action.payload;
    },
    setChatConnectionDetails: (state, action: PayloadAction<ChatConnectionDetails>) => {
      state.connectionDetails = action.payload;
    },
    setV2ConnectionState: (state, action: PayloadAction<ChatConnState>) => {
      state.v2ConnectionState = action.payload;
    },
    setMessageReactions: (
      state,
      {
        payload,
      }: PayloadAction<{
        serial: string;
        reactions: Record<string, { count: number; mine: boolean; reactorIds?: number[] }>;
      }>
    ) => {
      const id = state.messages.ids.find((mid) => state.messages.entities[mid]?.serial === payload.serial);
      if (id !== undefined) {
        messagesAdapter.updateOne(state.messages, { id, changes: { reactions: payload.reactions } });
      }
    },
    markMessageDeleted: (state, { payload }: PayloadAction<{ serial: string }>) => {
      const id = state.messages.ids.find((mid) => state.messages.entities[mid]?.serial === payload.serial);
      if (id !== undefined) {
        messagesAdapter.updateOne(state.messages, { id, changes: { deleted: true, reactions: undefined } });
      }
    },
  },
  extraReducers: (builder) => builder.addCase(eventRoomActions.cleanedUp, () => initialState),
});

export const {
  setChatActive,
  setChatScrolledToBottom,
  excludeFromMessages,
  markMessagesAsRejected,
  addMessages,
  markAsReadMessage,
  setChatInputText,
  setChatConnectionDetails,
  setV2ConnectionState,
  setMessageReactions,
  markMessageDeleted,
} = chatSlice.actions;
